import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, DragEvent, ComponentType } from 'react';
import type { Board as BoardData } from '@/api/Board';
import type { BoardLane } from '@/api/BoardLane';
import type { BoardCard } from '@/api/BoardCard';
import type { BoardTag } from '@/api/BoardTag';

interface LaneHeaderRendererProps {
  title: string;
  label?: string | null;
  laneId: string;
}

interface CardRendererProps extends BoardCard {
  laneId: string;
  cardColor?: string;
  cardStyle?: CSSProperties;
  className?: string;
  onDelete: () => void;
  onClick: () => void;
  onChange?: () => void;
  showDeleteButton?: boolean;
  t?: (key: string) => string;
}

type BoardEvent =
  | { type: 'UPDATE_CARD'; laneId: string; card: BoardCard }
  | { type: 'UPDATE_CARDS'; laneId: string; cards: BoardCard[] };

interface BoardEventBus {
  publish: (event: BoardEvent) => void;
}

interface TrelloBoardProps {
  data: BoardData;
  onDataChange?: (next: BoardData) => void;
  onCardClick?: (cardId: string, metadata: unknown, laneId: string) => void;
  onBeforeCardDelete?: (confirm: () => void) => void;
  onCardDelete?: (cardId: string, laneId: string) => void;
  eventBusHandle?: (bus?: BoardEventBus) => void;
  components?: {
    Card?: ComponentType<CardRendererProps>;
    LaneHeader?: ComponentType<LaneHeaderRendererProps>;
  };
  draggable?: boolean;
  laneDraggable?: boolean;
  editLaneTitle?: boolean;
  style?: CSSProperties;
}

const cloneTag = (tag: BoardTag): BoardTag => ({ ...tag });

const cloneCard = (card: BoardCard): BoardCard => ({
  ...card,
  tags: card.tags ? card.tags.map(cloneTag) : [],
  createdBy: card.createdBy ?? null,
  assignedTo: card.assignedTo ?? null,
  createdAt: card.createdAt ?? null,
  updatedAt: card.updatedAt ?? null,
});

const cloneBoard = (board: BoardData): BoardData => ({
  ...board,
  lanes: board.lanes.map(lane => ({
    ...lane,
    cards: lane.cards.map(cloneCard),
  })),
});

const DefaultLaneHeader = ({ title, label }: LaneHeaderRendererProps) => (
  <Box px='2' py='3'>
    <Text fontWeight='bold' fontSize='lg'>
      {title}
    </Text>
    {label ? (
      <Text fontSize='sm' color='gray.500'>
        {label}
      </Text>
    ) : null}
  </Box>
);

const DefaultCard = ({ title, description, onClick }: CardRendererProps) => (
  <Box
    onClick={onClick}
    cursor='pointer'
    bg='yellow.100'
    borderRadius='md'
    p='3'
    boxShadow='sm'
  >
    <Text fontWeight='semibold'>{title}</Text>
    {description ? (
      <Text fontSize='sm' mt='1' color='gray.600'>
        {description}
      </Text>
    ) : null}
  </Box>
);

const getInitialBoard = (data: BoardData): BoardData => cloneBoard(data);

const TrelloBoard = ({
  data,
  onDataChange,
  onCardClick,
  onBeforeCardDelete,
  onCardDelete,
  eventBusHandle,
  components,
  draggable = true,
  style,
}: TrelloBoardProps) => {
  const [board, setBoard] = useState<BoardData>(() => getInitialBoard(data));
  const [dragPayload, setDragPayload] = useState<{ laneId: string; cardId: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{ laneId: string; beforeCardId: string | null } | null>(null);
  const boardRef = useRef(board);

  useEffect(() => {
    const next = getInitialBoard(data);
    boardRef.current = next;
    setBoard(next);
  }, [data]);



  const updateBoardState = useCallback(
    (updater: (prev: BoardData) => BoardData | null) => {
      const nextState = updater(boardRef.current);
      if (!nextState) return null;
      boardRef.current = nextState;
      setBoard(nextState);
      onDataChange?.(cloneBoard(nextState));
      return nextState;
    },
    [onDataChange]
  );

  const handleBusEvent = useCallback(
    (event: BoardEvent) => {
      if (event.type === 'UPDATE_CARDS') {
        updateBoardState(prev => {
          const laneIndex = prev.lanes.findIndex(lane => lane.id === event.laneId);
          if (laneIndex === -1) return null;
          const nextLanes = prev.lanes.map((lane, index) =>
            index === laneIndex
              ? { ...lane, cards: event.cards.map(cloneCard) }
              : lane
          );
          return { ...prev, lanes: nextLanes };
        });
        return;
      }
      if (event.type === 'UPDATE_CARD') {
        updateBoardState(prev => {
          const laneIndex = prev.lanes.findIndex(lane => lane.id === event.laneId);
          if (laneIndex === -1) return null;
          const nextLanes = prev.lanes.map((lane, index) => {
            if (index !== laneIndex) return lane;
            const nextCards = lane.cards.map(card =>
              card.id === event.card.id ? cloneCard(event.card) : card
            );
            return { ...lane, cards: nextCards };
          });
          return { ...prev, lanes: nextLanes };
        });
      }
    },
    [updateBoardState]
  );

  const eventBus = useMemo<BoardEventBus>(() => ({ publish: handleBusEvent }), [handleBusEvent]);

  useEffect(() => {
    eventBusHandle?.(eventBus);
    return () => {
      eventBusHandle?.(undefined);
    };
  }, [eventBus, eventBusHandle]);

  const moveCard = useCallback(
    (sourceLaneId: string, targetLaneId: string, cardId: string, beforeCardId?: string | null) => {
      updateBoardState(prev => {
        const fromIndex = prev.lanes.findIndex(lane => lane.id === sourceLaneId);
        const toIndex = prev.lanes.findIndex(lane => lane.id === targetLaneId);
        if (fromIndex === -1 || toIndex === -1) return null;
        const lanes: BoardLane[] = prev.lanes.map(lane => ({ ...lane, cards: [...lane.cards] }));
        const sourceCards = lanes[fromIndex].cards as BoardCard[];
        const cardPosition = sourceCards.findIndex(card => card.id === cardId);
        if (cardPosition === -1) return null;
        const [card] = sourceCards.splice(cardPosition, 1);
        const targetCards = lanes[toIndex].cards as BoardCard[];
        let insertIndex = targetCards.length;
        if (beforeCardId) {
          const idx = targetCards.findIndex(item => item.id === beforeCardId);
          insertIndex = idx === -1 ? targetCards.length : idx;
        }
        targetCards.splice(insertIndex, 0, card);
        return { ...prev, lanes };
      });
    },
    [updateBoardState]
  );

  const readDragPayload = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (dragPayload) {
        return dragPayload;
      }
      try {
        const raw = event.dataTransfer.getData('application/json');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.laneId && parsed?.cardId) {
            return parsed as { laneId: string; cardId: string };
          }
        }
      } catch (_) {
        return null;
      }
      return null;
    },
    [dragPayload]
  );

  const handleLaneDrop = useCallback(
    (laneId: string) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const payload = readDragPayload(event);
      if (!payload) return;
      moveCard(payload.laneId, laneId, payload.cardId);
      setDragPayload(null);
      setDropTarget(null);
    },
    [moveCard, readDragPayload]
  );

  const handleCardDrop = useCallback(
    (laneId: string, beforeCardId: string) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const payload = readDragPayload(event);
      if (!payload) return;
      moveCard(payload.laneId, laneId, payload.cardId, beforeCardId);
      setDragPayload(null);
      setDropTarget(null);
    },
    [moveCard, readDragPayload]
  );

  const handleLaneDragOver = useCallback(
    (laneId: string) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (event.target !== event.currentTarget) return;
      setDropTarget(prev => {
        if (prev?.laneId === laneId && prev.beforeCardId === null) {
          return prev;
        }
        return { laneId, beforeCardId: null };
      });
    },
    []
  );

  const handleCardDragOver = useCallback(
    (laneId: string, beforeCardId: string) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDropTarget(prev => {
        if (prev?.laneId === laneId && prev.beforeCardId === beforeCardId) {
          return prev;
        }
        return { laneId, beforeCardId };
      });
    },
    []
  );

  const requestDelete = useCallback(
    (laneId: string, cardId: string) => {
      const executeDelete = () => {
        let removed = false;
        updateBoardState(prev => {
          const laneIndex = prev.lanes.findIndex(lane => lane.id === laneId);
          if (laneIndex === -1) return null;
          const lane = prev.lanes[laneIndex];
          const cardIndex = lane.cards.findIndex(card => card.id === cardId);
          if (cardIndex === -1) return null;
          const nextLanes = prev.lanes.map((entry, index) => {
            if (index !== laneIndex) return entry;
            const nextCards = entry.cards.slice();
            nextCards.splice(cardIndex, 1);
            return { ...entry, cards: nextCards };
          });
          removed = true;
          return { ...prev, lanes: nextLanes };
        });
        if (removed) {
          onCardDelete?.(cardId, laneId);
        }
      };

      if (onBeforeCardDelete) {
        onBeforeCardDelete(executeDelete);
      } else {
        executeDelete();
      }
    },
    [onBeforeCardDelete, onCardDelete, updateBoardState]
  );

  const CardRenderer = components?.Card ?? DefaultCard;
  const LaneHeader = components?.LaneHeader ?? DefaultLaneHeader;

  const DropIndicator = ({ visible = true }: { visible?: boolean }) => (
    <Box
      height='2'
      borderRadius='full'
      bg='blue.400'
      opacity={visible ? '0.6' : '0'}
      mx='2'
      boxShadow='0 0 0 1px rgba(49, 130, 206, 0.6)'
      pointerEvents='none'
    />
  );

  return (
    <Box w='full' overflowX='auto' style={style}>
      <Flex align='flex-start' gap='4'>
        {board.lanes.map(lane => (
          <Box
            key={lane.id}
            minW='280px'
            bg='whiteAlpha.900'
            borderRadius='lg'
            p='4'
            onDrop={handleLaneDrop(lane.id)}
            onDragOver={handleLaneDragOver(lane.id)}
          >
            <LaneHeader title={lane.title} label={lane.label} laneId={lane.id} />
            <Stack gap='3' mt='3' onDragOver={handleLaneDragOver(lane.id)}>
              {lane.cards.map(card => (
                <Fragment key={card.id}>
                  <Box
                    pt='3'
                    draggable={draggable}
                    onDragStart={event => {
                      event.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({ laneId: lane.id, cardId: card.id })
                      );
                      event.dataTransfer.effectAllowed = 'move';
                      setDragPayload({ laneId: lane.id, cardId: card.id });
                    }}
                    onDragEnd={() => {
                      setDragPayload(null);
                      setDropTarget(null);
                    }}
                    onClick={() => onCardClick?.(card.id, undefined, lane.id)}
                    onDrop={handleCardDrop(lane.id, card.id)}
                    onDragOver={handleCardDragOver(lane.id, card.id)}
                    position='relative'
                  >
                    <Box position='absolute' top='0' left='0' right='0'>
                      <DropIndicator
                        visible={dropTarget?.laneId === lane.id && dropTarget.beforeCardId === card.id}
                      />
                    </Box>
                    <CardRenderer
                      {...card}
                      laneId={lane.id}
                      cardColor='black'
                      onDelete={() => requestDelete(lane.id, card.id)}
                      onClick={() => onCardClick?.(card.id, undefined, lane.id)}
                      showDeleteButton={Boolean(onBeforeCardDelete || onCardDelete)}
                    />
                  </Box>
                </Fragment>
              ))}
              {dropTarget?.laneId === lane.id && dropTarget.beforeCardId === null ? (
                <DropIndicator />
              ) : null}
            </Stack>
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

export default TrelloBoard;
